'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState } from 'react';
import { authFetch, buildBackendUrl } from '@textile-erp/core';
import { DEFAULT_THEME, COLOR_PRESETS, FONT_FAMILIES, FONT_SIZES, SPACING_MODES } from '../types/theme';
export const ThemeContext = createContext(undefined);
// Helper function to adjust color intensity for contrast levels
function adjustColorIntensity(color, factor) {
    // Convert hex to RGB
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    // Adjust RGB values by factor
    const adjustedR = Math.min(255, Math.max(0, Math.round(r * factor)));
    const adjustedG = Math.min(255, Math.max(0, Math.round(g * factor)));
    const adjustedB = Math.min(255, Math.max(0, Math.round(b * factor)));
    // Convert back to hex
    const toHex = (n) => n.toString(16).padStart(2, '0');
    return `#${toHex(adjustedR)}${toHex(adjustedG)}${toHex(adjustedB)}`;
}
export function ThemeProvider({ children }) {
    const [theme, setTheme] = useState(DEFAULT_THEME);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    // Load theme from database on mount
    useEffect(() => {
        const loadThemeFromDatabase = async () => {
            try {
                const token = localStorage.getItem('textile_erp_token');
                if (!token) {
                    // No user logged in, use localStorage as fallback
                    const savedTheme = localStorage.getItem('textile-erp-theme');
                    if (savedTheme) {
                        const parsedTheme = JSON.parse(savedTheme);
                        setTheme({ ...DEFAULT_THEME, ...parsedTheme });
                    }
                    setIsLoaded(true);
                    return;
                }
                const response = await authFetch(buildBackendUrl('/api/theme/settings'), {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });
                if (response.ok) {
                    const result = await response.json();
                    if (result.success && result.data) {
                        // Convert database theme settings to theme config
                        const dbTheme = result.data;
                        const convertedTheme = {
                            mode: (dbTheme.mode === 'dark' || dbTheme.mode === 'light') ? dbTheme.mode : 'light',
                            colorPreset: dbTheme.primaryColor || 'blue',
                            layoutDensity: dbTheme.layoutDensity || 'standard',
                            sidebarMode: dbTheme.sidebarMode || 'integrated',
                            fontFamily: dbTheme.fontFamily === 'inter' ? 'sans' :
                                dbTheme.fontFamily === 'system' ? 'system' :
                                    dbTheme.fontFamily === 'serif' ? 'serif' :
                                        dbTheme.fontFamily === 'mono' ? 'mono' : 'sans',
                            fontSize: dbTheme.fontScale === 1.0 ? 'medium' :
                                dbTheme.fontScale < 1.0 ? 'small' : 'large',
                            spacingMode: dbTheme.spacingScale === 1.0 ? 'normal' :
                                dbTheme.spacingScale < 1.0 ? 'compact' : 'spacious',
                            backgroundContrast: dbTheme.backgroundContrast === 1.0 ? 'normal' :
                                dbTheme.backgroundContrast < 1.0 ? 'low' : 'high'
                        };
                        setTheme({ ...DEFAULT_THEME, ...convertedTheme });
                    }
                }
                else {
                    console.warn('Failed to load theme from database, using localStorage fallback');
                    const savedTheme = localStorage.getItem('textile-erp-theme');
                    if (savedTheme) {
                        const parsedTheme = JSON.parse(savedTheme);
                        setTheme({ ...DEFAULT_THEME, ...parsedTheme });
                    }
                }
            }
            catch (error) {
                console.error('Error loading theme from database:', error);
                // Fallback to localStorage
                const savedTheme = localStorage.getItem('textile-erp-theme');
                if (savedTheme) {
                    try {
                        const parsedTheme = JSON.parse(savedTheme);
                        setTheme({ ...DEFAULT_THEME, ...parsedTheme });
                    }
                    catch (parseError) {
                        console.error('Failed to parse saved theme:', parseError);
                    }
                }
            }
            finally {
                setIsLoaded(true);
                setIsInitialLoad(false);
            }
        };
        loadThemeFromDatabase();
    }, []);
    // Save theme to database whenever it changes
    useEffect(() => {
        const saveThemeToDatabase = async () => {
            if (!isLoaded || isSaving || isInitialLoad)
                return;
            const token = localStorage.getItem('textile_erp_token');
            if (!token) {
                // No user logged in, save to localStorage
                localStorage.setItem('textile-erp-theme', JSON.stringify(theme));
                return;
            }
            setIsSaving(true);
            try {
                // Convert theme config to database format
                const dbThemeData = {
                    mode: theme.mode,
                    primaryColor: theme.colorPreset,
                    layoutDensity: theme.layoutDensity,
                    sidebarMode: theme.sidebarMode,
                    fontFamily: theme.fontFamily === 'sans' ? 'inter' :
                        theme.fontFamily === 'system' ? 'system' :
                            theme.fontFamily === 'serif' ? 'serif' :
                                theme.fontFamily === 'mono' ? 'mono' : 'inter',
                    fontScale: theme.fontSize === 'small' ? 0.9 :
                        theme.fontSize === 'large' ? 1.1 :
                            theme.fontSize === 'extra-large' ? 1.2 : 1.0,
                    spacingScale: theme.spacingMode === 'compact' ? 0.9 :
                        theme.spacingMode === 'spacious' ? 1.1 : 1.0,
                    backgroundContrast: theme.backgroundContrast === 'low' ? 0.8 :
                        theme.backgroundContrast === 'high' ? 1.2 : 1.0,
                    enableAnimations: true, // Always enabled for now
                    enableGlass: false, // Disabled by default
                    reducedMotion: false, // Could be detected from system preferences
                    highContrast: false // Could be detected from system preferences
                };
                const response = await authFetch(buildBackendUrl('/api/theme/settings'), {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(dbThemeData),
                });
                if (!response.ok) {
                    console.warn('Failed to save theme to database, saving to localStorage as fallback');
                    localStorage.setItem('textile-erp-theme', JSON.stringify(theme));
                }
            }
            catch (error) {
                console.error('Error saving theme to database:', error);
                // Fallback to localStorage
                localStorage.setItem('textile-erp-theme', JSON.stringify(theme));
            }
            finally {
                setIsSaving(false);
            }
        };
        saveThemeToDatabase();
    }, [theme, isLoaded]);
    // Apply theme to CSS variables
    useEffect(() => {
        if (!isLoaded)
            return;
        const root = document?.documentElement;
        if (!root)
            return;
        const colorScheme = COLOR_PRESETS[theme.colorPreset] || COLOR_PRESETS.blue;
        const fontFamily = FONT_FAMILIES[theme.fontFamily] || FONT_FAMILIES.sans;
        const fontSize = FONT_SIZES[theme.fontSize] || FONT_SIZES.medium;
        const spacingMode = SPACING_MODES[theme.spacingMode] || SPACING_MODES.normal;
        // Apply color variables
        Object.entries(colorScheme.primary).forEach(([shade, color]) => {
            root.style.setProperty(`--color-primary-${shade}`, color);
        });
        Object.entries(colorScheme.secondary).forEach(([shade, color]) => {
            root.style.setProperty(`--color-secondary-${shade}`, color);
        });
        Object.entries(colorScheme.tertiary).forEach(([shade, color]) => {
            root.style.setProperty(`--color-tertiary-${shade}`, color);
        });
        // Apply theme mode
        root.style.setProperty('--theme-mode', theme.mode);
        // Set dynamic background colors based on theme, palette, and contrast
        if (theme.useCustomBackgroundColors && theme.backgroundPalette) {
            // Generate colors dynamically based on current theme preset and style
            const currentColors = colorScheme;
            let paletteColors;
            const isOpposite = theme.backgroundPalette?.includes('-opposite');
            const baseStyle = theme.backgroundPalette?.replace('-opposite', '');
            // Generate base colors based on style and theme mode
            if (theme.mode === 'light') {
                switch (baseStyle) {
                    case 'subtle':
                        paletteColors = isOpposite ? {
                            primary: currentColors.secondary[50],
                            secondary: currentColors.tertiary[50],
                            accent: currentColors.primary[100]
                        } : {
                            primary: currentColors.primary[50],
                            secondary: currentColors.primary[100],
                            accent: currentColors.secondary[50]
                        };
                        break;
                    case 'vibrant':
                        paletteColors = isOpposite ? {
                            primary: currentColors.secondary[200],
                            secondary: currentColors.tertiary[200],
                            accent: currentColors.primary[300]
                        } : {
                            primary: currentColors.primary[200],
                            secondary: currentColors.primary[300],
                            accent: currentColors.secondary[200]
                        };
                        break;
                    case 'muted':
                        paletteColors = isOpposite ? {
                            primary: currentColors.primary[50],
                            secondary: currentColors.secondary[50],
                            accent: currentColors.primary[100]
                        } : {
                            primary: currentColors.tertiary[50],
                            secondary: currentColors.tertiary[100],
                            accent: currentColors.tertiary[200]
                        };
                        break;
                    case 'inverted':
                        paletteColors = isOpposite ? {
                            primary: currentColors.secondary[800],
                            secondary: currentColors.tertiary[800],
                            accent: currentColors.primary[700]
                        } : {
                            primary: currentColors.primary[800],
                            secondary: currentColors.primary[700],
                            accent: currentColors.secondary[600]
                        };
                        break;
                    default:
                        paletteColors = {
                            primary: currentColors.primary[50],
                            secondary: currentColors.primary[100],
                            accent: currentColors.secondary[50]
                        };
                }
            }
            else {
                switch (baseStyle) {
                    case 'subtle':
                        paletteColors = isOpposite ? {
                            primary: currentColors.secondary[900],
                            secondary: currentColors.tertiary[900],
                            accent: currentColors.primary[800]
                        } : {
                            primary: currentColors.primary[900],
                            secondary: currentColors.primary[800],
                            accent: currentColors.secondary[900]
                        };
                        break;
                    case 'vibrant':
                        paletteColors = isOpposite ? {
                            primary: currentColors.secondary[700],
                            secondary: currentColors.tertiary[700],
                            accent: currentColors.primary[600]
                        } : {
                            primary: currentColors.primary[700],
                            secondary: currentColors.primary[600],
                            accent: currentColors.secondary[700]
                        };
                        break;
                    case 'muted':
                        paletteColors = isOpposite ? {
                            primary: currentColors.primary[900],
                            secondary: currentColors.secondary[900],
                            accent: currentColors.primary[800]
                        } : {
                            primary: currentColors.tertiary[900],
                            secondary: currentColors.tertiary[800],
                            accent: currentColors.tertiary[700]
                        };
                        break;
                    case 'inverted':
                        paletteColors = isOpposite ? {
                            primary: currentColors.secondary[200],
                            secondary: currentColors.tertiary[200],
                            accent: currentColors.primary[300]
                        } : {
                            primary: currentColors.primary[200],
                            secondary: currentColors.primary[300],
                            accent: currentColors.secondary[400]
                        };
                        break;
                    default:
                        paletteColors = {
                            primary: currentColors.primary[900],
                            secondary: currentColors.primary[800],
                            accent: currentColors.secondary[900]
                        };
                }
            }
            let primaryColor = paletteColors.primary;
            let secondaryColor = paletteColors.secondary;
            let accentColor = paletteColors.accent;
            // Apply contrast adjustments
            if (theme.backgroundContrast === 'high') {
                primaryColor = adjustColorIntensity(primaryColor, theme.mode === 'dark' ? 1.2 : 0.8);
                secondaryColor = adjustColorIntensity(secondaryColor, theme.mode === 'dark' ? 1.3 : 0.7);
                accentColor = adjustColorIntensity(accentColor, theme.mode === 'dark' ? 1.4 : 0.6);
            }
            else if (theme.backgroundContrast === 'low') {
                primaryColor = adjustColorIntensity(primaryColor, theme.mode === 'dark' ? 0.7 : 1.1);
                secondaryColor = adjustColorIntensity(secondaryColor, theme.mode === 'dark' ? 0.8 : 1.05);
                accentColor = adjustColorIntensity(accentColor, theme.mode === 'dark' ? 0.9 : 1.02);
            }
            root.style.setProperty('--theme-bg-primary', primaryColor);
            root.style.setProperty('--theme-bg-secondary', secondaryColor);
            root.style.setProperty('--theme-bg-accent', accentColor);
        }
        else {
            // Use default background colors based on color scheme and contrast level
            if (theme.mode === 'light') {
                const bgColors = {
                    low: {
                        primary: '#ffffff',
                        secondary: '#f9fafb',
                        accent: '#fef7f0'
                    },
                    normal: {
                        primary: colorScheme.tertiary[50],
                        secondary: colorScheme.tertiary[100],
                        accent: colorScheme.primary[50]
                    },
                    high: {
                        primary: colorScheme.tertiary[200],
                        secondary: colorScheme.tertiary[300],
                        accent: colorScheme.primary[200]
                    }
                };
                const contrastKey = theme.backgroundContrast || 'normal';
                const selectedColors = bgColors[contrastKey] || bgColors.normal;
                root.style.setProperty('--theme-bg-primary', selectedColors.primary);
                root.style.setProperty('--theme-bg-secondary', selectedColors.secondary);
                root.style.setProperty('--theme-bg-accent', selectedColors.accent);
            }
            else {
                const bgColors = {
                    low: {
                        primary: '#000000',
                        secondary: '#0a0a0a',
                        accent: '#1a1a1a'
                    },
                    normal: {
                        primary: colorScheme.tertiary[900],
                        secondary: colorScheme.tertiary[800],
                        accent: colorScheme.primary[900]
                    },
                    high: {
                        primary: colorScheme.tertiary[700],
                        secondary: colorScheme.tertiary[600],
                        accent: colorScheme.primary[700]
                    }
                };
                const contrastKey = theme.backgroundContrast || 'normal';
                const selectedColors = bgColors[contrastKey] || bgColors.normal;
                root.style.setProperty('--theme-bg-primary', selectedColors.primary);
                root.style.setProperty('--theme-bg-secondary', selectedColors.secondary);
                root.style.setProperty('--theme-bg-accent', selectedColors.accent);
            }
        }
        // Apply font size
        root.style.setProperty('--font-scale', fontSize.scale);
        // Apply spacing mode
        root.style.setProperty('--spacing-scale', spacingMode.scale);
        // Apply layout density
        root.style.setProperty('--layout-density', theme.layoutDensity);
        // Apply dark/light mode classes — treat any non-explicit mode as 'light'
        // (prevents the OS prefers-color-scheme from auto-toggling the app theme)
        const resolvedMode = theme.mode === 'dark' ? 'dark' : 'light';
        if (document?.documentElement) {
            document.documentElement.classList.toggle('dark', resolvedMode === 'dark');
            document.documentElement.classList.toggle('light', resolvedMode === 'light');
        }
        // Also apply dark class to body for portal elements
        if (document?.body) {
            document.body.classList.toggle('dark', resolvedMode === 'dark');
            document.body.classList.toggle('light', resolvedMode === 'light');
        }
        // Apply font family class
        if (document?.body) {
            document.body.className = document.body.className
                .replace(/font-(system|sans|serif|mono)/g, '')
                .trim();
            document.body.classList.add(fontFamily.cssClass);
        }
        // Apply sidebar mode
        root.style.setProperty('--sidebar-mode', theme.sidebarMode);
    }, [theme, isLoaded]);
    // Load Google Fonts
    useEffect(() => {
        const fontFamily = FONT_FAMILIES[theme.fontFamily];
        if (fontFamily.googleFont) {
            const existingLink = document?.querySelector(`link[data-font="${theme.fontFamily}"]`);
            if (!existingLink && document) {
                const link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.googleFont}&display=swap`;
                link.setAttribute('data-font', theme.fontFamily);
                document.head.appendChild(link);
            }
        }
    }, [theme.fontFamily]);
    const updateTheme = (updates) => {
        setTheme(prevTheme => {
            const newTheme = { ...prevTheme, ...updates };
            return newTheme;
        });
    };
    const resetTheme = () => {
        setTheme(DEFAULT_THEME);
    };
    const toggleMode = () => {
        setTheme(prevTheme => ({
            ...prevTheme,
            mode: prevTheme.mode === 'light' ? 'dark' : 'light'
        }));
    };
    const contextValue = {
        theme,
        updateTheme,
        resetTheme,
        toggleMode,
    };
    return (_jsx(ThemeContext.Provider, { value: contextValue, children: children }));
}
export function useTheme() {
    const context = useContext(ThemeContext);
    // Return default values if used outside ThemeProvider (graceful fallback)
    if (context === undefined) {
        return {
            theme: DEFAULT_THEME,
            updateTheme: () => { },
            resetTheme: () => { },
            toggleMode: () => { },
        };
    }
    return context;
}
